import { useState, useEffect, useContext } from 'react';
import api from '../api/Base';
import archiver from '../api/Archiver';
import StatisticApi from '../api/host/StatisticApi';
import { ActivityData, StatisticData, EventData } from '../views/dashboard/types';
import { EventCounts } from '../types/instance/InstanceTarget';
import { HostStatsSummary } from '../api/host/StatisticApi';
import { TimeRangeContext } from '../context/TimeRangeContext';
import { showError } from '../utils/showAlert';
import { API_DATE_FORMAT, buildApiQuery } from '../helpers/timeRangeUtils';

interface DashboardDataState {
    activityData: Record<number, ActivityData>;
    executionsData: Record<number, StatisticData>;
    eventCountsData: Record<number, EventCounts>;
    hostStatsMap: Record<number, HostStatsSummary>;
}

/**
 * Custom hook for fetching dashboard metrics data
 * @param instances - Array of instances
 * @param datasourceIdToHostIdMap - Mapping of datasource IDs to host IDs (from useInstanceFilters)
 */
const useDashboardData = (instances: any[], datasourceIdToHostIdMap: Record<number, number[]>) => {
    const timeRangeContext = useContext(TimeRangeContext);
    
    const [state, setState] = useState<DashboardDataState>({
        activityData: {},
        executionsData: {},
        eventCountsData: {},
        hostStatsMap: {}
    });

    // Fetch metrics data
    useEffect(() => {
        const fetchData = async () => {
            const from = timeRangeContext.timeRange.from.format(API_DATE_FORMAT);
            const to = timeRangeContext.timeRange.to.format(API_DATE_FORMAT);
            const tz = timeRangeContext.timeRange.timezone;
            const limit = 501;
            const interval = timeRangeContext.timeRange.interval || 0;

            const activityUrl = `/activity/id?limit=${limit}&from=${from}&to=${to}&tz=${tz}&interval=${interval}&sort=waittime+desc`;
            const executionsUrl = `/statistic/id?limit=${limit}&from=${from}&to=${to}&tz=${tz}&interval=${interval}&statistic=executions`;
            const eventUrl = `/rest/v2/event?from=${from}&to=${to}&tz=${tz}&interval=${interval}&sort=timeslice+desc&limit=10000`;

            try {
                const [
                    activityResponse,
                    executionsResponse,
                    eventResponse
                ] = await Promise.all([
                    api.get<ActivityData[]>(activityUrl),
                    api.get<StatisticData[]>(executionsUrl),
                    archiver.get<EventData[]>(eventUrl)
                ]);

                const activityMap = activityResponse.data.reduce((acc, item) => {
                    acc[item.id] = item;
                    return acc;
                }, {} as Record<number, ActivityData>);

                const executionsMap = executionsResponse.data.reduce((acc, item) => {
                    acc[item.id] = item;
                    return acc;
                }, {} as Record<number, StatisticData>);

                const eventCountsMap = eventResponse.data.reduce((acc, eventItem) => {
                    const dsId = eventItem.datasourceid;
                    if (dsId === 0) return acc;

                    if (!acc[dsId]) {
                        acc[dsId] = { alert: 0, change: 0, custom: 0 };
                    }

                    switch (eventItem.event) {
                        case 'alert':
                            acc[dsId].alert += 1;
                            break;
                        case 'change':
                            acc[dsId].change += 1;
                            break;
                        case 'custom':
                            acc[dsId].custom += 1;
                            break;
                    }
                    return acc;
                }, {} as Record<number, EventCounts>);

                setState(prev => ({
                    ...prev,
                    activityData: activityMap,
                    executionsData: executionsMap,
                    eventCountsData: eventCountsMap
                }));

            } catch (error: any) {
                console.error("Failed to fetch estate view data:", error);
                showError('Failed to fetch dashboard data', error.message || error.toString());
                setState(prev => ({
                    ...prev,
                    activityData: {},
                    executionsData: {},
                    eventCountsData: {},
                    hostStatsMap: {}
                }));
            }
        };

        fetchData();
    }, [timeRangeContext.timeRange]);

    // Fetch host statistics
    useEffect(() => {
        const fetchHostStats = async () => {
            const hostIdsToFetch = Array.from(
                new Set(
                    instances
                        .map(inst => datasourceIdToHostIdMap[inst.id])
                        .filter((ids): ids is number[] => !!ids)
                        .flat()
                )
            );

            if (hostIdsToFetch.length === 0 || Object.keys(datasourceIdToHostIdMap).length === 0) {
                setState(prev => ({ ...prev, hostStatsMap: {} }));
                return;
            }

            try {
                const timeRangeQueryString = buildApiQuery(timeRangeContext.timeRange);
                const stats = await StatisticApi.getHostStatisticsSummaryBatch(timeRangeQueryString, hostIdsToFetch);
                setState(prev => ({ ...prev, hostStatsMap: stats }));
            } catch (error: any) {
                console.error("Failed to fetch host statistics batch:", error);
                showError('Failed to fetch host statistics', error.message || error.toString());
                setState(prev => ({ ...prev, hostStatsMap: {} }));
            }
        };

        if (instances.length > 0 && Object.keys(datasourceIdToHostIdMap).length > 0) {
            fetchHostStats();
        } else {
            setState(prev => ({ ...prev, hostStatsMap: {} }));
        }
    }, [instances, datasourceIdToHostIdMap, timeRangeContext.timeRange]);

    return state;
};

export default useDashboardData;
